'use client';

import { useEffect, useRef } from 'react';

type ScriptExecutorProps = {
  script: string;
  nonce: string | undefined;
  handleHtmlComplete?: () => void;
};

export default function ScriptExecutor({
  script,
  nonce,
  handleHtmlComplete,
}: ScriptExecutorProps) {
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current || !script?.trim()) {
      handleHtmlComplete?.();
      return;
    }
    didRunRef.current = true;

    const wrap = document.createElement('div');
    wrap.innerHTML = script;

    const all = Array.from(wrap.querySelectorAll('script'));
    const externals = all.filter(n => !!n.src);
    const inlines = all.filter(n => !n.src);

    const loadExternalSeq = externals.reduce<Promise<void>>(
      (p, node) =>
        p.then(
          () =>
            new Promise<void>(resolve => {
              const s = document.createElement('script');
              if (nonce) s.setAttribute('nonce', nonce);
              Array.from(node.attributes).forEach(a =>
                s.setAttribute(a.name, a.value)
              );
              s.async = false;
              const t = window.setTimeout(resolve, 10000);
              s.onload = () => {
                window.clearTimeout(t);
                resolve();
              };
              s.onerror = () => {
                window.clearTimeout(t);
                resolve();
              };
              document.body.appendChild(s);
            })
        ),
      Promise.resolve()
    );

    loadExternalSeq
      .then(() =>
        inlines.reduce<Promise<void>>(
          (p, node) =>
            p.then(() => {
              const s = document.createElement('script');
              Array.from(node.attributes).forEach(a =>
                s.setAttribute(a.name, a.value)
              );
              s.text = node.textContent || '';
              document.body.appendChild(s);
              return Promise.resolve();
            }),
          Promise.resolve()
        )
      )
      .then(() => {
        requestAnimationFrame(() => {
          handleHtmlComplete?.();
        });
      })
      .catch(e => {
        requestAnimationFrame(() => {
          handleHtmlComplete?.();
        });
        console.error('[ScriptExecutor] error', e);
      });
  }, [script, nonce, handleHtmlComplete]);

  return null;
}
